import React from 'react';
import classNames from 'classnames/bind';

import { useStream } from 'streams/hooks/useStream';
import { useUpdateStream } from 'streams/hooks/useUpdateStream';

import { CopyToClipboardButton } from 'components/CopyToClipboardButton/CopyToClipboardButton';
import { EditableText } from 'components/EditableText/EditableText';
import { IconButton } from 'components/IconButton/IconButtonDeprecated';
import { Loader } from 'components/Loader/Loader';
import { Text } from 'components/Text/Text';

import { StreamSnapshotListElement } from './StreamSnapshotListElement';

import styles from './CameraDetailStreams.module.scss';

const c = classNames.bind(styles);

type CameraStreamListElementProps = {
  streamID: string;
  hideLoader?: boolean;
};

export function CameraStreamListElement({
  streamID,
  hideLoader,
}: CameraStreamListElementProps) {
  const { data: stream } = useStream(streamID);
  const { mutate: updateStream } = useUpdateStream();
  const [isEditing, setIsEditing] = React.useState(false);
  const inputRef = React.useRef<HTMLTextAreaElement>(null);

  React.useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  function handleSave(name: string) {
    if (stream && name.trim() && name !== stream.name) {
      updateStream({ id: stream.id, name: name.trim() });
    }
    setIsEditing(false);
  }

  function handleCancel() {
    setIsEditing(false);
  }

  if (!stream && hideLoader) {
    return null;
  }

  if (!stream) {
    return <Loader text="Loading stream..." />;
  }

  return (
    <li className={c('stream', 'stream-container')}>
      <StreamSnapshotListElement streamId={stream.id} />
      <div className={c('stream-info')}>
        <div className={c('stream-name')}>
          {isEditing ? (
            <EditableText
              defaultValue={stream.name}
              onSave={handleSave}
              onCancel={handleCancel}
              ref={inputRef}
            />
          ) : (
            <>
              <strong>{stream.name}</strong>
              <IconButton
                icon="edit"
                label="Rename stream"
                size="xsmall"
                onClick={() => setIsEditing(true)}
              />
            </>
          )}
          <CopyToClipboardButton
            text={String(stream.id)}
            inline
            label="Copy stream ID"
          />
        </div>
        <Text className={c('stream-uri')}>{stream.uri}</Text>
      </div>
    </li>
  );
}
